import type { ThemeColorName } from '@examdojo/styles';
import { JsxParamStore } from '@examdojo/core/widget';
import type { ElementDisplayConfig, FunctionElementDef } from '../../../graphpad.model';
import type { ElementContext, FunctionElement } from '../../element.model';
import { createHandlesFromConfigs } from '../../features/handle';
import { BaseFunctionCreator } from '../function.model';
import { CUBIC_META, type CubicParams } from './cubic.model';

/** Cubic params with index signature for JsxParamStore compatibility */
type CubicParamsIndexed = CubicParams & { [key: string]: number };

/**
 * Creates cubic functions in vertex form: a(x-h)³ + k
 *
 * Parameters:
 * - a: vertical scale/reflection
 * - h: horizontal shift (inflection point x-coordinate)
 * - k: vertical shift (inflection point y-coordinate)
 */
export class CubicCreator extends BaseFunctionCreator<CubicParamsIndexed> {
  readonly functionType = 'cubic';
  readonly defaultParams: CubicParamsIndexed = { ...CUBIC_META.defaultParams };
  readonly availableFeatures = CUBIC_META.availableFeatures;
  readonly handleConfigs = CUBIC_META.handleConfigs;

  create(
    id: string,
    def: FunctionElementDef,
    display: ElementDisplayConfig | undefined,
    context: ElementContext,
  ): FunctionElement {
    const { board, elementFactory, themeService } = context;
    const initialParams = { ...this.defaultParams, ...def.params } as CubicParamsIndexed;

    const colorName = (display?.curve?.color ?? 'indigo') as ThemeColorName;
    const strokeColor = themeService.getColor(colorName);

    // ------------------------------------------------
    // PARAMETER STORE
    // ------------------------------------------------
    const paramStore = new JsxParamStore(board, {
      a: { value: initialParams.a, range: CUBIC_META.defaultRanges.a },
      h: { value: initialParams.h, range: CUBIC_META.defaultRanges.h },
      k: { value: initialParams.k, range: CUBIC_META.defaultRanges.k },
    });

    // ------------------------------------------------
    // HANDLE CREATION
    // ------------------------------------------------
    const { handles, handlePoints } = createHandlesFromConfigs(
      board,
      paramStore,
      this.handleConfigs,
      initialParams,
      strokeColor,
    );

    // ------------------------------------------------
    // EVALUATE FUNCTION
    // ------------------------------------------------
    const evaluate = (x: number): number => {
      const a = paramStore.getValue('a');
      const h = paramStore.getValue('h');
      const k = paramStore.getValue('k');
      return a * Math.pow(x - h, 3) + k;
    };

    // ------------------------------------------------
    // CURVE CREATION
    // ------------------------------------------------
    const curveResult = elementFactory.curve({
      type: 'functiongraph',
      fn: evaluate,
      name: id,
      style: display?.curve,
    });

    const objects = [...paramStore.getAllSliders(), ...handlePoints, curveResult.curve];
    if (curveResult.label) {
      objects.push(curveResult.label);
    }

    // ------------------------------------------------
    // DERIVATIVE
    // ------------------------------------------------
    // Derivative of a(x-h)³ + k is 3a(x-h)²
    const getDerivative = (order = 1): ((x: number) => number) => {
      if (order === 1) {
        return (x: number) => {
          const a = paramStore.getValue('a');
          const h = paramStore.getValue('h');
          return 3 * a * Math.pow(x - h, 2);
        };
      }
      if (order === 2) {
        return (x: number) => {
          const a = paramStore.getValue('a');
          const h = paramStore.getValue('h');
          return 6 * a * (x - h);
        };
      }
      if (order === 3) {
        return () => {
          const a = paramStore.getValue('a');
          return 6 * a;
        };
      }
      // Higher order derivatives are 0 for cubic
      return () => 0;
    };

    return {
      id,
      type: 'cubic',
      objects,
      evaluate,
      handleConfigs: this.handleConfigs,
      handles,
      getParams: () => paramStore.getParams() as CubicParamsIndexed,
      setParams: (p) => paramStore.setParams(p as Record<string, number>),
      getDerivative,
      availableFeatures: this.availableFeatures,
      destroy: () => {
        objects.forEach((obj) => board.removeObject(obj));
      },
    };
  }
}
